/**
 * Catalog of Operations in-module views (history / new) plus outbound
 * day-of-work links. Day queues live on Inicio. Mailbox, pickups, quote and
 * cases stay top-level nav: other roles use them. The desk only points at them.
 */
import type { LucideIcon } from 'lucide-react';
import {
  CalendarClock,
  ClipboardList,
  History,
  Mail,
  PlusCircle,
  Shield,
  TrendingUp,
} from 'lucide-react';
import type { PickupSummary } from '../../types';
import { ROUTES } from '../routes';

export const OPERATIONS_VIEW_IDS = ['history', 'form', 'daily'] as const;
export type OperationsViewId = (typeof OPERATIONS_VIEW_IDS)[number];

export interface OperationsNavItemDef {
  view: OperationsViewId;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

export interface OperationsNavGroupDef {
  id: string;
  groupLabelKey: string;
  items: OperationsNavItemDef[];
}

export interface OperationsDayLinkDef {
  to: string;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  routeKey: 'mailbox' | 'pickups' | 'quote' | 'cases';
}

export const OPERATIONS_NAV_GROUPS: OperationsNavGroupDef[] = [
  {
    id: 'lookup',
    groupLabelKey: 'operations.nav.groups.lookup',
    items: [
      {
        view: 'history',
        icon: History,
        labelKey: 'operations.nav.items.history.label',
        descriptionKey: 'operations.nav.items.history.description',
      },
      {
        view: 'form',
        icon: PlusCircle,
        labelKey: 'operations.nav.items.form.label',
        descriptionKey: 'operations.nav.items.form.description',
      },
      {
        view: 'daily',
        icon: ClipboardList,
        labelKey: 'operations.nav.items.daily.label',
        descriptionKey: 'operations.nav.items.daily.description',
      },
    ],
  },
];

export const OPERATIONS_DAY_LINKS: OperationsDayLinkDef[] = [
  {
    to: ROUTES.mailbox,
    icon: Mail,
    routeKey: 'mailbox',
    labelKey: 'operations.nav.items.mailbox.label',
    descriptionKey: 'operations.nav.items.mailbox.description',
  },
  {
    to: ROUTES.pickups,
    icon: CalendarClock,
    routeKey: 'pickups',
    labelKey: 'operations.nav.items.pickups.label',
    descriptionKey: 'operations.nav.items.pickups.description',
  },
  {
    to: `${ROUTES.quote}?view=form`,
    icon: TrendingUp,
    routeKey: 'quote',
    labelKey: 'operations.nav.items.quote.label',
    descriptionKey: 'operations.nav.items.quote.description',
  },
  {
    to: ROUTES.cases,
    icon: Shield,
    routeKey: 'cases',
    labelKey: 'operations.nav.items.cases.label',
    descriptionKey: 'operations.nav.items.cases.description',
  },
];

export function operationsPath(view: OperationsViewId): string {
  return `${ROUTES.operations}?view=${view}`;
}

export function operationsItemFor(view: string): OperationsNavItemDef | undefined {
  return OPERATIONS_NAV_GROUPS[0]?.items.find((item) => item.view === view);
}

export function operationsSidebarSubItems() {
  return OPERATIONS_NAV_GROUPS.flatMap((group) =>
    group.items.map((navItem, index) => ({
      id: operationsPath(navItem.view),
      labelKey: navItem.labelKey,
      icon: navItem.icon,
      descriptionKey: navItem.descriptionKey,
      ...(index === 0 ? { groupLabelKey: group.groupLabelKey } : {}),
    })),
  );
}

/** Pickups that still need an operator: scheduled, in progress, or not collected. */
export function pickupFollowUpCount(summary: PickupSummary): number {
  return (Number(summary.scheduled) || 0)
    + (Number(summary.inProgress) || 0)
    + (Number(summary.notCompleted) || 0);
}

export function caseFollowUpCount(stats: { needsFollowUp?: number; vencidos?: number; estancados?: number }): number {
  if (typeof stats.needsFollowUp === 'number') return stats.needsFollowUp;
  return (Number(stats.vencidos) || 0) + (Number(stats.estancados) || 0);
}
